"use client";

import type { FormEvent } from "react";

const decode = (value: string) => value
  .replace(/&lt;/g, "<")
  .replace(/&gt;/g, ">")
  .replace(/&amp;/g, "&")
  .replace(/&quot;/g, '"')
  .replace(/&#39;/g, "'");

const encode = (value: string) => value
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

const text = (tag: string, source: string) => {
  const value = source.match(new RegExp(`<${tag}\\b[^>]*>([\\s\\S]*?)</${tag}>`, "i"))?.[1] ?? "";
  return decode(value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim());
};

const blocks = (tag: string, source: string) => [
  ...source.matchAll(new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?</${tag}>`, "gi")),
].map((match) => match[0]);

const replaceTag = (source: string, tag: string, value: string) => {
  const replacement = `<${tag}>${encode(value)}</${tag}>`;
  const pattern = new RegExp(`<${tag}\\b[^>]*>[\\s\\S]*?</${tag}>`, "i");
  return pattern.test(source) ? source.replace(pattern, replacement) : source;
};

const fieldClass = "mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-[12px] font-semibold text-gray-800 outline-none transition-colors focus:border-seexr-primary dark:border-[#343439] dark:bg-[#202023] dark:text-gray-100";

const display = (value: string) => value || "현장 입력";

export function TbmScenarioReport({
  xmlContent,
  isEditing,
  onSave,
}: {
  xmlContent: string;
  isEditing: boolean;
  onSave: (xml: string) => void | Promise<void>;
}) {
  const agenda = blocks("step", text("agenda", xmlContent) ? (xmlContent.match(/<agenda\b[^>]*>[\s\S]*?<\/agenda>/i)?.[0] ?? "") : "");
  const topics = blocks("topic", xmlContent);
  const priority = blocks("priority_risk", xmlContent)[0] ?? "";
  const slam = blocks("slam", xmlContent)[0] ?? "";
  const health = blocks("health_check", xmlContent)[0] ?? "";
  const ppe = blocks("ppe_check", xmlContent)[0] ?? "";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const updateBlock = (block: string, fields: Array<[string, string]>) => {
      let updated = block;
      for (const [tag, fieldName] of fields) {
        const value = data.get(fieldName);
        if (typeof value === "string") updated = replaceTag(updated, tag, value);
      }
      return updated;
    };
    let next = xmlContent;
    next = next.replace(/<work_overview\b[^>]*>[\s\S]*?<\/work_overview>/i, (block) =>
      updateBlock(block, [["work_name", "work_name"], ["work_description", "work_description"]]));
    let agendaIndex = 0;
    next = next.replace(/<step\b[^>]*>[\s\S]*?<\/step>/gi, (block) => {
      const index = agendaIndex++;
      return updateBlock(block, [
        ["minute", `agenda-${index}-minute`],
        ["title", `agenda-${index}-title`],
        ["leader_script", `agenda-${index}-leader-script`],
      ]);
    });
    let topicIndex = 0;
    next = next.replace(/<topic\b[^>]*>[\s\S]*?<\/topic>/gi, (block) => {
      const index = topicIndex++;
      return updateBlock(block, [
        ["hazard", `topic-${index}-hazard`],
        ["cause", `topic-${index}-cause`],
        ["controls", `topic-${index}-controls`],
        ["leader_script", `topic-${index}-leader-script`],
        ["open_question", `topic-${index}-open-question`],
        ["understanding_check", `topic-${index}-understanding-check`],
      ]);
    });
    next = next.replace(/<priority_risk\b[^>]*>[\s\S]*?<\/priority_risk>/i, (block) =>
      updateBlock(block, [["one_point_message", "priority_message"], ["required_action", "priority_action"]]));
    next = next.replace(/<health_check\b[^>]*>[\s\S]*?<\/health_check>/i, (block) =>
      updateBlock(block, [["leader_script", "health_script"]]));
    next = next.replace(/<ppe_check\b[^>]*>[\s\S]*?<\/ppe_check>/i, (block) =>
      updateBlock(block, [["required_ppe", "required_ppe"], ["leader_script", "ppe_script"]]));
    next = next.replace(/<slam\b[^>]*>[\s\S]*?<\/slam>/i, (block) =>
      updateBlock(block, [["stop", "slam_stop"], ["look", "slam_look"], ["assess", "slam_assess"], ["manage", "slam_manage"]]));
    for (const tag of ["actual_incident_case", "stop_work_rule", "communication_support", "closing"]) {
      const value = data.get(tag);
      if (typeof value === "string") next = replaceTag(next, tag, value);
    }
    await onSave(next);
  };

  return (
    <form id="tbm-scenario-form" onSubmit={handleSubmit} className="space-y-5">
      <section className="rounded-2xl border border-seexr-100 bg-seexr-50/70 p-5 dark:border-seexr-900/40 dark:bg-seexr-950/15">
        <p className="text-[10px] font-black uppercase tracking-widest text-seexr-500">10-minute TBM</p>
        {isEditing ? <><input name="work_name" defaultValue={text("work_name", xmlContent)} aria-label="작업명" className={`${fieldClass} text-[15px]`} /><textarea name="work_description" defaultValue={text("work_description", xmlContent)} aria-label="작업 설명" rows={3} className={fieldClass} /></> : <><h3 className="mt-2 text-lg font-black text-gray-950 dark:text-white">{display(text("work_name", xmlContent))}</h3><p className="mt-2 text-[13px] font-medium leading-6 text-gray-600 dark:text-gray-300">{display(text("work_description", xmlContent))}</p></>}
      </section>

      <section>
        <h4 className="mb-3 text-[13px] font-black text-gray-800 dark:text-gray-200">진행 순서</h4>
        <div className="grid gap-3 md:grid-cols-2">
          {agenda.map((step, index) => (
            <div key={index} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-[#2a2a2e] dark:bg-[#161618]">
              <span className="text-[10px] font-black text-seexr-primary">{display(text("minute", step))}분</span>
              {isEditing ? <><input name={`agenda-${index}-minute`} defaultValue={text("minute", step)} aria-label={`${index + 1}단계 시간`} className={fieldClass} /><input name={`agenda-${index}-title`} defaultValue={text("title", step)} aria-label={`${index + 1}단계 제목`} className={fieldClass} /><textarea name={`agenda-${index}-leader-script`} defaultValue={text("leader_script", step)} aria-label={`${index + 1}단계 진행자 멘트`} rows={3} className={fieldClass} /></> : <><p className="mt-1 text-[13px] font-black text-gray-900 dark:text-gray-100">{display(text("title", step))}</p><p className="mt-2 text-[12px] font-medium leading-5 text-gray-500 dark:text-gray-400">{display(text("leader_script", step))}</p></>}
            </div>
          ))}
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-[13px] font-black text-gray-800 dark:text-gray-200">오늘 확인할 핵심 위험</h4>
        <div className="space-y-3">
          {topics.map((topic, index) => (
            <div key={index} className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-[#2a2a2e] dark:bg-[#161618]">
              <div className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-seexr-solid/10 text-[11px] font-black text-seexr-primary">{index + 1}</span>
                <div className="min-w-0">
                  {isEditing ? <div className="space-y-2"><input name={`topic-${index}-hazard`} defaultValue={text("hazard", topic)} aria-label={`${index + 1}번 위험요인`} className={fieldClass} /><textarea name={`topic-${index}-cause`} defaultValue={text("cause", topic)} aria-label={`${index + 1}번 원인`} rows={2} className={fieldClass} /><textarea name={`topic-${index}-controls`} defaultValue={text("controls", topic)} aria-label={`${index + 1}번 대책`} rows={3} className={fieldClass} /><textarea name={`topic-${index}-leader-script`} defaultValue={text("leader_script", topic)} aria-label={`${index + 1}번 진행자 멘트`} rows={3} className={fieldClass} /><input name={`topic-${index}-open-question`} defaultValue={text("open_question", topic)} aria-label={`${index + 1}번 열린 질문`} className={fieldClass} /><input name={`topic-${index}-understanding-check`} defaultValue={text("understanding_check", topic)} aria-label={`${index + 1}번 이해 확인`} className={fieldClass} /></div> : <><p className="text-[14px] font-black text-gray-900 dark:text-gray-100">{display(text("hazard", topic))}</p><p className="mt-2 text-[12px] font-medium leading-5 text-gray-500 dark:text-gray-400">{display(text("controls", topic))}</p><div className="mt-3 rounded-xl bg-gray-50 px-3 py-2.5 text-[11px] font-bold leading-5 text-gray-600 dark:bg-[#202023] dark:text-gray-300">질문: {display(text("open_question", topic))}</div></>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-900/40 dark:bg-amber-950/15">
        <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">One Point</p>
        {isEditing ? <><input name="priority_message" defaultValue={text("one_point_message", priority)} aria-label="최우선 위험 메시지" className={fieldClass} /><textarea name="priority_action" defaultValue={text("required_action", priority)} aria-label="최우선 위험 필수 조치" rows={3} className={fieldClass} /></> : <><p className="mt-2 text-[15px] font-black text-gray-950 dark:text-white">{display(text("one_point_message", priority))}</p><p className="mt-2 text-[12px] font-semibold leading-5 text-gray-600 dark:text-gray-300">{display(text("required_action", priority))}</p></>}
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-[#2a2a2e] dark:bg-[#161618]">
        <h4 className="text-[13px] font-black text-gray-900 dark:text-gray-100">SLAM 작업 전 확인</h4>
        <div className="mt-3 grid gap-2 sm:grid-cols-4">
          {[["Stop", "stop"], ["Look", "look"], ["Assess", "assess"], ["Manage", "manage"]].map(([label, tag]) => (
            <div key={tag} className="rounded-xl bg-gray-50 p-3 dark:bg-[#202023]"><p className="text-[10px] font-black text-seexr-primary">{label}</p>{isEditing ? <textarea name={`slam_${tag}`} defaultValue={text(tag, slam)} aria-label={`SLAM ${label}`} rows={3} className={fieldClass} /> : <p className="mt-1 text-[11px] font-semibold leading-5 text-gray-600 dark:text-gray-300">{display(text(tag, slam))}</p>}</div>
          ))}
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        {[
          ['health_script', '건강상태 확인 멘트', text('leader_script', health)],
          ['required_ppe', '필수 보호구', text('required_ppe', ppe)],
          ['ppe_script', '보호구 확인 멘트', text('leader_script', ppe)],
          ['actual_incident_case', '현장 실제 사례', text('actual_incident_case', xmlContent)],
          ['stop_work_rule', '작업중지·보고 기준', text('stop_work_rule', xmlContent)],
          ['communication_support', '의사소통 지원', text('communication_support', xmlContent)],
          ['closing', '마무리 멘트', text('closing', xmlContent)],
        ].map(([name, label, value]) => <label key={name} className="rounded-2xl border border-gray-200 bg-white p-4 text-[11px] font-black text-gray-500 dark:border-[#2a2a2e] dark:bg-[#161618]">{label}{isEditing ? <textarea name={name} defaultValue={value} rows={3} className={fieldClass} /> : <p className="mt-2 text-[12px] font-medium leading-5 text-gray-700 dark:text-gray-200">{display(value)}</p>}</label>)}
      </section>
    </form>
  );
}

export function TbmRecordReport({
  xmlContent,
  isEditing,
  onSave,
}: {
  xmlContent: string;
  isEditing: boolean;
  onSave: (xml: string) => void | Promise<void>;
}) {
  const hazards = blocks("hazard_entry", xmlContent);
  const checklist = blocks("check_item", xmlContent);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    let next = xmlContent;
    for (const tag of [
      "date", "time", "same_as_work_date", "location", "leader_affiliation",
      "leader_title", "leader_name", "leader_signature", "daily_safety_inspection",
      "questions_and_suggestions", "follow_up_result", "end_of_work_meeting",
    ]) {
      const value = data.get(tag);
      if (typeof value === "string") next = replaceTag(next, tag, value);
    }
    let hazardIndex = 0;
    next = next.replace(/<hazard_entry\b[^>]*>[\s\S]*?<\/hazard_entry>/gi, (block) => {
      const index = hazardIndex++;
      const action = data.get(`hazard-${index}-action`);
      const followUp = data.get(`hazard-${index}-follow-up`);
      let updated = block;
      if (typeof action === "string") updated = replaceTag(updated, "pre_work_action_result", action);
      if (typeof followUp === "string") updated = replaceTag(updated, "follow_up", followUp);
      return updated;
    });
    next = next.replace(/<check_item\b([^>]*)>[\s\S]*?<\/check_item>/gi, (block, attributes: string) => {
      const id = attributes.match(/\bid=["']([^"']+)["']/i)?.[1] ?? "";
      const result = data.get(`check-${id}-result`);
      const action = data.get(`check-${id}-action`);
      let updated = block;
      if (typeof result === "string") updated = replaceTag(updated, "result", result);
      if (typeof action === "string") updated = replaceTag(updated, "action", action);
      return updated;
    });
    const attendee = blocks("attendee", next)[0];
    if (attendee) {
      let updated = attendee;
      for (const tag of ["name", "affiliation", "signature"]) {
        const value = data.get(`attendee_${tag}`);
        if (typeof value === "string") updated = replaceTag(updated, tag, value);
      }
      next = next.replace(attendee, updated);
    }
    await onSave(next);
  };

  return (
    <form id="tbm-record-form" onSubmit={handleSubmit} className="space-y-5">
      <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-[#2a2a2e] dark:bg-[#161618]">
        <h3 className="text-[15px] font-black text-gray-950 dark:text-white">TBM 기본 정보</h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {[['date', '작업일', 'date'], ['time', '회의 시간', 'time'], ['location', '작업 장소', 'text'], ['leader_name', '진행자', 'text'], ['leader_affiliation', '소속', 'text'], ['leader_title', '직책', 'text']].map(([tag, label, inputType]) => (
            <label key={tag} className="text-[11px] font-black text-gray-500">{label}
              {isEditing ? <input name={tag} type={inputType} defaultValue={text(tag, xmlContent)} className={fieldClass} /> : <p className="mt-1.5 rounded-xl bg-gray-50 px-3 py-2.5 text-[12px] font-semibold text-gray-700 dark:bg-[#202023] dark:text-gray-200">{display(text(tag, xmlContent))}</p>}
            </label>
          ))}
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-[13px] font-black text-gray-800 dark:text-gray-200">위험요인 및 작업 전 조치</h4>
        <div className="space-y-3">
          {hazards.map((hazard, index) => (
            <div key={index} className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-[#2a2a2e] dark:bg-[#161618]">
              <p className="text-[13px] font-black text-gray-900 dark:text-gray-100">{display(text("hazard", hazard))}</p>
              <p className="mt-2 text-[12px] font-medium leading-5 text-gray-500 dark:text-gray-400">대책: {display(text("control_measure", hazard))}</p>
              {isEditing ? <div className="mt-3 grid gap-3 sm:grid-cols-2"><label className="text-[11px] font-black text-gray-500">작업 전 조치 결과<input name={`hazard-${index}-action`} defaultValue={text("pre_work_action_result", hazard)} className={fieldClass} /></label><label className="text-[11px] font-black text-gray-500">후속 조치<input name={`hazard-${index}-follow-up`} defaultValue={text("follow_up", hazard)} className={fieldClass} /></label></div> : <p className="mt-3 text-[11px] font-bold text-seexr-primary">조치 결과: {display(text("pre_work_action_result", hazard))}</p>}
            </div>
          ))}
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-[13px] font-black text-gray-800 dark:text-gray-200">실행 체크리스트</h4>
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-[#2a2a2e] dark:bg-[#161618]">
          {checklist.map((item, index) => {
            const id = item.match(/<check_item\b[^>]*\bid=["']([^"']+)["']/i)?.[1] ?? String(index);
            return <div key={id} className="grid gap-2 border-b border-gray-100 p-4 last:border-b-0 dark:border-[#29292d] sm:grid-cols-[1fr_130px_1fr] sm:items-center"><div><span className="text-[9px] font-black text-seexr-primary">{display(text("phase", item))}</span><p className="mt-1 text-[12px] font-bold leading-5 text-gray-700 dark:text-gray-200">{display(text("label", item))}</p></div>{isEditing ? <><select name={`check-${id}-result`} defaultValue={text("result", item)} className={fieldClass}><option value="">선택</option><option value="예">예</option><option value="아니오">아니오</option><option value="해당 없음">해당 없음</option></select><input name={`check-${id}-action`} defaultValue={text("action", item)} placeholder="조치 내용" className={fieldClass} /></> : <><span className="text-[11px] font-black text-gray-600 dark:text-gray-300">{display(text("result", item))}</span><span className="text-[11px] font-medium text-gray-500">{display(text("action", item))}</span></>}</div>;
          })}
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        {[['daily_safety_inspection', '일일 안전점검'], ['questions_and_suggestions', '질문 및 건의사항'], ['follow_up_result', '조치 결과 피드백'], ['end_of_work_meeting', '작업 종료 미팅']].map(([tag, label]) => <label key={tag} className="rounded-2xl border border-gray-200 bg-white p-4 text-[11px] font-black text-gray-500 dark:border-[#2a2a2e] dark:bg-[#161618]">{label}{isEditing ? <textarea name={tag} defaultValue={text(tag, xmlContent)} rows={3} className={fieldClass} /> : <p className="mt-2 text-[12px] font-medium leading-5 text-gray-700 dark:text-gray-200">{display(text(tag, xmlContent))}</p>}</label>)}
      </section>

      <section className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-[#2a2a2e] dark:bg-[#161618]">
        <h4 className="text-[13px] font-black text-gray-900 dark:text-gray-100">참석자 확인</h4>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">{[['name', '이름'], ['affiliation', '소속'], ['signature', '서명 확인']].map(([tag, label]) => <label key={tag} className="text-[11px] font-black text-gray-500">{label}{isEditing ? <input name={`attendee_${tag}`} defaultValue={text(tag, blocks("attendee", xmlContent)[0] ?? "")} className={fieldClass} /> : <p className="mt-1.5 text-[12px] font-semibold text-gray-700 dark:text-gray-200">{display(text(tag, blocks("attendee", xmlContent)[0] ?? ""))}</p>}</label>)}</div>
      </section>
    </form>
  );
}
